// Third party libs/components
import React, {
  useState,
  useEffect,
  useRef,
  useLayoutEffect,
  useMemo,
} from 'react';
import { useSelector } from 'react-redux';
import InputMask from 'react-input-mask';
import * as yup from 'yup';
import { useForm, Controller } from 'react-hook-form';
import PropTypes from 'prop-types';

//Components
import Loader from '@components/Loader';
import CreditCardInput from '@components/CreditCardInput';
import DlocalInput from './DlocalInput';
import Brands from '../../Brands';

//Style
import * as Styled from '../../../CreditCardForm.styles';

//Utils
import useDlocal from '@utils/useDlocal';
import { translate as t } from '@utils/translate';
import { getCurrentLang } from '@utils/language';

const smartFieldSchema = {
  complete: yup.boolean().required(),
  brand: yup.string().required(),
};

const smartFieldFunc = function(data) {
  return data?.complete === true;
};

const checkAllFieldsIsMounted = fields => {
  return Promise.all(
    fields.map(field => {
      return new Promise(resolve => {
        field.on('ready', () => resolve());
      });
    })
  );
};

const smartFieldParameters = {
  pan: {
    style: {
      base: {
        fontSize: '16px',
      },
    },
    placeholder: '4111 1111 1111 1111',
    hideIcon: true,
    refName: 'panRef',
    saveFunction: 'setPanField',
  },
  cvv: {
    style: {
      base: {
        fontSize: '16px',
      },
    },
    refName: 'cvvRef',
    saveFunction: 'setCvvField',
  },
  expiration: {
    style: {
      base: {
        fontSize: '16px',
      },
    },
    refName: 'expiryDateRef',
    saveFunction: 'setExpirationField',
  },
};

const smartFieldCreator = (fieldName, createField, ref, setPanField) => {
  let field = createField(fieldName, smartFieldParameters[fieldName]);
  field.mount(ref.current);
  setPanField(field);
  return field;
};

const smartFieldDestructor = dlocalFields => {
  if (dlocalFields?.length) {
    dlocalFields.forEach(element => {
      element.unmount();
    });
  }
};

const DlocalForm = ({
  onCreate,
  onChange,
  onCancel,
  allowedCountries,
  paymentGateway,
  hidden,
  countryDocumentInfo,
}) => {
  const { metadata } = paymentGateway;

  const language = getCurrentLang();

  const { creditCardFormData } = useSelector(state => state.cart);

  const TEXTS = useMemo(
    () => ({
      NEW_CARD: t('newCard'),
      NUMBER: t('cardNumber'),
      CVV: t('cvv'),
      NAME: t('cardHolderName'),
      EXPIRATION_DATE: t('validity'),
      HOLDER_DOCUMENT: t('docs'),
      CANCEL: t('cancel'),
      SAVE: t('saveButton'),
      WAIT: t('addCardWaitMsg'),
      ADDED_CARD: t('addedCard'),
      CONTINUE: t('next'),
      COUNTRY: t('country'),
      INVALID_CARD_NUMBER: t('invalidCardNumber'),
    }),
    [language]
  );

  const schema = useMemo(
    () => ({
      country: {
        name: 'country',
        type: 'text',
        label: TEXTS.COUNTRY,
      },
      pan: {
        name: 'pan',
        type: 'text',
        required: true,
        type: 'text',
        placeholder: '4111 1111 1111 1111',
        label: TEXTS.NUMBER,
      },
      expiration: {
        name: 'expiration',
        type: 'text',
        placeholder: 'MM/AA',
        label: TEXTS.EXPIRATION_DATE,
        mask: '99/99',
        style: {
          width: '100%',
        },
      },
      cvv: {
        name: 'cvv',
        type: 'text',
        placeholder: 'CVV',
        label: TEXTS.CVV,
        style: {
          width: '100%',
        },
      },
      holderName: {
        name: 'holderName',
        type: 'text',
        label: TEXTS.NAME,
      },
      holderDocument: {
        name: 'holderDocument',
        type: 'text',
        autoFocus: true,
      },
    }),
    [language]
  );

  const validationSchema = useMemo(
    () =>
      yup
        .object()
        .shape({
          pan: yup
            .object(smartFieldSchema)
            .test('is-smart-input-complete-1', null, smartFieldFunc)
            .required(TEXTS.REQUIRED_FIELD),
          expiration: yup
            .object(smartFieldSchema)
            .test('is-smart-input-complete-2', null, smartFieldFunc)
            .required(TEXTS.REQUIRED_FIELD),
          cvv: yup
            .object(smartFieldSchema)
            .test('is-smart-input-complete-3', null, smartFieldFunc)
            .required(TEXTS.REQUIRED_FIELD),
          country: yup.string().required(TEXTS.REQUIRED_FIELD),
          holderName: yup
            .string()
            .min(2, null)
            .required(TEXTS.REQUIRED_FIELD),
          holderDocument: yup
            .string()
            .max(18, null)
            .required(TEXTS.REQUIRED_FIELD),
        })
        .required(),
    [language]
  );

  const {
    register,
    handleSubmit,
    getValues,
    watch,
    control,
    formState,
  } = useForm({
    validationSchema,
    mode: 'onChange',
    shouldUseNativeValidation: false,
    defaultValues: {
      country: creditCardFormData?.country,
      pan: {},
      expiration: {},
      cvv: {},
      holderName: '',
      holderDocument: '',
    },
  });

  const {
    dlocalInstance,
    createField,
    createToken,
    getInformation,
  } = useDlocal({
    scriptUrl: metadata.scriptUrl,
    apiKey: metadata.smartFieldApiKey,
    fieldSettings: {
      locale: 'PT',
      country: creditCardFormData.country,
    },
  });

  const [panField, setPanField] = useState(null);
  const [cvvField, setCvvField] = useState(null);
  const [expirationField, setExpirationField] = useState(null);
  const [mountingField, setMountingField] = useState(true);
  const [isFormValid, setIsFormValid] = useState(false);
  const [dlocalFields, setDlocalFields] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const panRef = useRef(null);
  const cvvRef = useRef(null);
  const expiryDateRef = useRef(null);

  useLayoutEffect(() => {
    setIsLoading(true);
    if (dlocalFields != null) {
      smartFieldDestructor(dlocalFields);
    }

    if (dlocalInstance) {
      const refs = { panRef, cvvRef, expiryDateRef };
      const saveFunction = { setPanField, setCvvField, setExpirationField };
      let fieldsCreated = [];
      for (const fieldName in smartFieldParameters) {
        const [newFieldRefName, newFieldSaveFunction] = [
          refs[smartFieldParameters[fieldName].refName],
          saveFunction[smartFieldParameters[fieldName].saveFunction],
        ];
        const newField = smartFieldCreator(
          fieldName,
          createField,
          newFieldRefName,
          newFieldSaveFunction
        );
        fieldsCreated.push(newField);
      }
      if (mountingField) {
        checkAllFieldsIsMounted(fieldsCreated).then(() => {
          setMountingField(false);
          setIsLoading(false);
          setDlocalFields(fieldsCreated);
        });
      }
    }
    return () => smartFieldDestructor(dlocalFields);
  }, [dlocalInstance]);

  useEffect(() => {
    if ((formState.isValid === formState.dirty) === true) {
      setIsFormValid(true);
    } else {
      setIsFormValid(false);
    }
  }, [watch()]);

  useEffect(() => {
    const value = getValues(schema.country.name);
    onChange({
      ...creditCardFormData,
      [schema.country.name]: value,
    });
  }, [getValues(schema.country.name)]);

  const onSubmit = async data => {
    setIsLoading(true);
    const panFieldInfo = await getInformation(panField);
    const panFieldToken = await createToken(panField, {
      name: data.holderName,
    });

    delete data.pan;
    delete data.expiration;
    delete data.cvv;

    onChange({
      cardType: data?.pan?.brand || null,
      token: panFieldToken?.token,
      numberDlocal: panFieldInfo?.bin,
      ...data,
    });
    onCreate();
    setMountingField(false);
    setIsLoading(false);
  };

  return (
    <>
      <Styled.HeaderContainer>
        <Styled.Title>{TEXTS.NEW_CARD}</Styled.Title>
        <Brands flagsToExclude={['ELO']} />
      </Styled.HeaderContainer>
      <Styled.Form
        show={!mountingField && !hidden}
        onSubmit={handleSubmit(onSubmit)}
        noValidate
      >
        <Styled.InputContainer>
          <Styled.InputColumn>
            <CreditCardInput>
              <select
                defaultValue={creditCardFormData.country || ''}
                ref={register}
                {...schema.country}
              >
                {allowedCountries &&
                  allowedCountries.map(({ country }) => {
                    return (
                      <option value={country.iso2Code} key={country.iso2Code}>
                        {country.name}
                      </option>
                    );
                  })}
              </select>
              <span>{TEXTS.COUNTRY}</span>
            </CreditCardInput>

            <CreditCardInput>
              <Controller
                as={DlocalInput}
                control={control}
                input={panField}
                inputRef={panRef}
                {...schema.pan}
              />
              <span>{TEXTS.NUMBER}</span>
            </CreditCardInput>

            <Styled.InputRow>
              <CreditCardInput>
                <Controller
                  as={DlocalInput}
                  control={control}
                  input={expirationField}
                  inputRef={expiryDateRef}
                  {...schema.expiration}
                />
                <span>{TEXTS.EXPIRATION_DATE}</span>
              </CreditCardInput>

              <CreditCardInput>
                <Controller
                  as={DlocalInput}
                  control={control}
                  input={cvvField}
                  inputRef={cvvRef}
                  {...schema.cvv}
                >
                  <span>{TEXTS.CVV}</span>
                </Controller>
              </CreditCardInput>
            </Styled.InputRow>
          </Styled.InputColumn>

          <Styled.InputColumn>
            <CreditCardInput>
              <input
                defaultValue={creditCardFormData.holderName || ''}
                ref={register}
                {...schema.holderName}
              />
              <span>{TEXTS.NAME}</span>
            </CreditCardInput>

            <CreditCardInput>
              <Controller
                as={InputMask}
                control={control}
                defaultValue={creditCardFormData.holderDocument || ''}
                mask={countryDocumentInfo?.mask}
                {...schema.holderDocument}
              />
              <span>{countryDocumentInfo?.name || TEXTS.HOLDER_DOCUMENT}</span>
            </CreditCardInput>
          </Styled.InputColumn>
        </Styled.InputContainer>

        <Styled.BtnContainer>
          <Styled.Button onClick={onCancel} theme="default" type="button">
            {TEXTS.CANCEL}
          </Styled.Button>
          <Styled.Button
            theme={isFormValid ? 'active' : 'disabled'}
            type="submit"
          >
            {TEXTS.SAVE}
          </Styled.Button>
        </Styled.BtnContainer>
      </Styled.Form>
      <Loader show={isLoading} />
    </>
  );
};

DlocalForm.propTypes = {
  getFormData: PropTypes.func.isRequired,
  onCreate: PropTypes.func.isRequired,
  onChange: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  allowedCountries: PropTypes.array.isRequired,
  paymentGateway: PropTypes.object.isRequired,
  hidden: PropTypes.bool.isRequired,
  countryDocumentInfo: PropTypes.object.isRequired,
};

export default DlocalForm;
